type GoogleDayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

type WeekDayValue =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

type GoogleOpeningHourTime = {
  day?: number | string;
  time?: string | number;
};

type GoogleOpeningHourPeriod = {
  open?: GoogleOpeningHourTime;
  close?: GoogleOpeningHourTime;
};

type GoogleOpeningHours = {
  periods?: GoogleOpeningHourPeriod[];
  weekday_text?: string[];
};

const GOOGLE_DAY_TO_WEEKDAY: Record<GoogleDayIndex, WeekDayValue> = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
};

const INTERNAL_WEEKDAY_ORDER: WeekDayValue[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const CLOSED_DAY_DEFAULT = {
  startTime: "08:00 AM",
  endTime: "18:00 AM",
  open: false,
} as const;

const MISSING_CLOSE_TIME_DEFAULT = "10:00 PM";

const normalizeGoogleTime = (value?: string | number): string | null => {
  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }

  const valueStr = String(value).trim();
  if (!/^\d{1,4}$/.test(valueStr)) {
    return null;
  }

  return valueStr.padStart(4, "0");
};

const GOOGLE_DAY_ENUM_TO_INDEX: Record<string, GoogleDayIndex> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const getGoogleDayIndex = (day?: number | string): GoogleDayIndex | null => {
  if (typeof day === "number" && Number.isInteger(day) && day >= 0 && day <= 6) {
    return day as GoogleDayIndex;
  }

  if (typeof day !== "string") return null;

  const trimmedDay = day.trim().toLowerCase();
  if (/^[0-6]$/.test(trimmedDay)) return Number(trimmedDay) as GoogleDayIndex;

  const withoutPrefix = trimmedDay.replace(/^day_of_week_/, "");
  return GOOGLE_DAY_ENUM_TO_INDEX[withoutPrefix] ?? null;
};

const getDayOfWeekByGoogleIndex = (day?: number | string): WeekDayValue | null => {
  const googleDayIndex = getGoogleDayIndex(day);
  if (googleDayIndex === null) return null;

  return GOOGLE_DAY_TO_WEEKDAY[googleDayIndex];
};

const hasSinglePeriod24hPattern = (periods?: GoogleOpeningHourPeriod[]) => {
  const openDayIndex = getGoogleDayIndex(periods?.[0].open?.day);
  return (
    periods?.length === 1 &&
    openDayIndex === 0 &&
    normalizeGoogleTime(periods[0].open?.time) === "0000" &&
    !periods[0].close
  );
};

const hasSevenDays24hPattern = (periods?: GoogleOpeningHourPeriod[]) => {
  if (periods?.length !== 7) return false;

  const expectedDays = new Set([0, 1, 2, 3, 4, 5, 6]);

  return periods.every((period) => {
    const openDay = getGoogleDayIndex(period.open?.day);
    const closeDay = getGoogleDayIndex(period.close?.day);
    const openTime = normalizeGoogleTime(period.open?.time);
    const closeTime = normalizeGoogleTime(period.close?.time);

    if (openDay === null || !expectedDays.has(openDay)) {
      return false;
    }
    expectedDays.delete(openDay);

    const closesSameDayAt2359 = closeDay === openDay && closeTime === "2359";
    const closesNextDayAtMidnight =
      closeDay === (openDay + 1) % 7 && closeTime === "0000";

    return openTime === "0000" && (closesSameDayAt2359 || closesNextDayAtMidnight);
  });
};

export function isOpen24By7(opening_hours?: GoogleOpeningHours): boolean {
  if (!opening_hours) return false;

  // Keep textual check for legacy payloads that only include weekday_text.
  const allDays24hByText =
    opening_hours.weekday_text?.length === 7 &&
    opening_hours.weekday_text.every((text) =>
      text.toLowerCase().includes("open 24 hours")
    );

  return (
    allDays24hByText ||
    hasSinglePeriod24hPattern(opening_hours.periods) ||
    hasSevenDays24hPattern(opening_hours.periods)
  );
}

export function formatHour(hora: string | number): string {
  const normalizedHour = normalizeGoogleTime(hora);
  if (!normalizedHour) return "12:00 AM";

  const horas = parseInt(normalizedHour.slice(0, 2), 10);
  const minutos = normalizedHour.slice(2);

  const periodo = horas >= 12 ? "PM" : "AM";
  const horas12 = horas % 12 === 0 ? 12 : horas % 12;

  return `${horas12.toString().padStart(2, "0")}:${minutos} ${periodo}`;
}

export function parseGoogleOpeningHours(
  opening_hours?: GoogleOpeningHours
): Array<{
  dayOfWeek: WeekDayValue;
  startTime: string;
  endTime: string;
  open: boolean;
}> {
  const periodsByDay = new Map<WeekDayValue, Array<{
    dayOfWeek: WeekDayValue;
    startTime: string;
    endTime: string;
    open: boolean;
  }>>();

  INTERNAL_WEEKDAY_ORDER.forEach((day) => periodsByDay.set(day, []));

  opening_hours?.periods?.forEach((period) => {
    const dayOfWeek = getDayOfWeekByGoogleIndex(period.open?.day);
    const startTimeRaw = normalizeGoogleTime(period.open?.time);

    if (!dayOfWeek || !startTimeRaw) return;

    const endTimeRaw = normalizeGoogleTime(period.close?.time);
    const dayPeriods = periodsByDay.get(dayOfWeek);
    if (!dayPeriods) return;

    dayPeriods.push({
      dayOfWeek,
      startTime: formatHour(startTimeRaw),
      endTime: endTimeRaw ? formatHour(endTimeRaw) : MISSING_CLOSE_TIME_DEFAULT,
      open: true,
    });
  });

  return INTERNAL_WEEKDAY_ORDER.flatMap((dayOfWeek) => {
    const dayPeriods = periodsByDay.get(dayOfWeek) || [];
    if (dayPeriods.length > 0) return dayPeriods;

    return [
      {
        dayOfWeek,
        startTime: CLOSED_DAY_DEFAULT.startTime,
        endTime: CLOSED_DAY_DEFAULT.endTime,
        open: CLOSED_DAY_DEFAULT.open,
      },
    ];
  });
}
